import React, { useEffect, useState } from "react";
import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { Skeleton, Drawer, Space, Badge } from "antd";
import { PlusCircleOutlined, RotateLeftOutlined } from "@ant-design/icons";
import { Link, useParams, useSearchParams } from "react-router-dom";
import OtherErrors from "@general/otherErrors";
import moment from "moment-jalaali";
import AdminCardsListTable from "./table";
import TableFilters from "@general/tableFilters";
import getWindowSize from "@utils/windowSize";
import AddNewCard from "./addNewRecord";
import CardControl from "./cardControl";
export const GETCARDS = gql`
  query ($paginateInput: PaginateInput!, $searchInput: CardSearchInput!) {
    getCardsCount(searchInput: $searchInput)
    getCards(paginateInput: $paginateInput, searchInput: $searchInput) {
      id
      createdAt
      updatedAt
      deactivatedAt
      cardNo
      cardIdentifier
      deliveredAt
      issuedAt
      description
      cardStatus
      user {
        id
        name
        mobile
        userRole
      }
    }
  }
`;

export const GET_ADMIN_CARD_STATISTICS = gql`
  query GetAdminCardStatistics {
    totalIssued: getCardsCount(
      searchInput: { cardStatus: [DELIVERING, DELIVERED] }
    )
    activeIssued: getCardsCount(
      searchInput: {
        cardStatus: [DELIVERING, DELIVERED]
        isDeactivated: false
      }
    )
  }
`;

function AdminAllUsersCards(props) {
  const [tableData, setTableData] = useState({});
  const [currentPage, setCurrentPage] = useState();
  const [sortDir, setSortDir] = useState("desc");
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [filterDateFrom, setFilterDateFrom] = useState(null);
  const [filterDateTo, setFilterDateTo] = useState(null);
  const [filterSearchPhrase, setFilterSearchPhrase] = useState("");
  const [drawer, setDrawer] = useState(false);
  const [detailsDerawer, setDetailsDrawer] = useState(false);
  const [addRecordsDerawer, setAddRecordsDrawer] = useState(false);
  const windowSize = getWindowSize().innerWidth;

  const {
    data: statsData,
    loading: statsLoading,
    refetch: refetchCardStats,
  } = useQuery(GET_ADMIN_CARD_STATISTICS, {
    fetchPolicy: "no-cache",
  });

  const totalIssued = statsData?.totalIssued ?? 0;
  const activeIssued = statsData?.activeIssued ?? 0;
  const inactiveIssued = Math.max(0, totalIssued - activeIssued);

  const [getCards, cards] = useLazyQuery(GETCARDS, {
    fetchPolicy: "no-cache",
    onCompleted(data) {
      setTableData(data);
    },
  });

  const { result } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const filteredCardStatus = searchParams.get("cardStatus");
  useEffect(() => {
    getCards({
      variables: {
        paginateInput: {
          page: currentPage,
          perPage: 10,
          sort: sortColumn && sortColumn,
          sortDir: sortDir,
        },
        searchInput: {
          userId: result !== "all" ? result : null,
          createdAt:
            filterDateFrom && filterDateTo
              ? [filterDateFrom, filterDateTo]
              : null,
          textSearch: filterSearchPhrase,
          cardStatus: filteredCardStatus,
        },
      },
    });
  }, [currentPage, sortDir, filterDateTo, filterSearchPhrase]);

  const dataUpdated = () => {
    getCards({
      variables: {
        paginateInput: {
          page: currentPage,
          perPage: 10,
          sort: sortColumn && sortColumn,
          sortDir: sortDir,
        },
        searchInput: {
          userId: result !== "all" ? result : null,
          createdAt:
            filterDateFrom && filterDateTo
              ? [filterDateFrom, filterDateTo]
              : null,
          textSearch: filterSearchPhrase,
          cardStatus: filteredCardStatus,
        },
      },
    });
    if (typeof refetchCardStats === "function") {
      refetchCardStats();
    }
  };

  const pageChange = (current) => {
    setCurrentPage(current);
  };

  const sort = (sortData) => {
    setCurrentPage(1);
    setSortColumn(sortData.columnKey);
    if (sortData.order === "ascend") setSortDir("asc");
    if (!sortData.order || sortData.order === "descend") setSortDir("desc");
  };

  const filterDate = (dates, dateString) => {
    setCurrentPage(1);
    if (dateString[0] === "" || dateString[1] === "") {
      setFilterDateFrom(null);
      setFilterDateTo(null);
    } else {
      setFilterDateFrom(
        moment(dateString[0], "jYYYY/jMM/jDD")
          .startOf("day")
          .toDate()
          .toISOString()
      );
      setFilterDateTo(
        moment(dateString[1], "jYYYY/jMM/jDD")
          .endOf("day")
          .toDate()
          .toISOString()
      );
    }
  };

  const filterSearch = (phrase) => {
    setCurrentPage(1);
    setFilterSearchPhrase(phrase);
  };

  const openDrawer = (id, name) => {
    setDrawer({ id, name });
  };
  const closeDrawer = () => {
    setDrawer(false);
  };
  if (cards.error) return <OtherErrors />;
  if (Object.keys(tableData).length === 0)
    return (
      <>
        <Skeleton active round />
        <br />
        <Skeleton active round />
      </>
    );
  return (
    <Badge.Ribbon
      style={{
        left: "-32px",
        height: "40px",
        display: "flex",
        alignItems: "center",
        cursor: "pointer"
      }}
      text= <div onClick={() => setAddRecordsDrawer(true)}>
      <span style={{ padding: "10px", color: "white" }}>
        <PlusCircleOutlined /> ثبت کارت جدید
      </span>
    </div>
    >
      <>
      <Drawer
          title="ثبت کارت جدید"
          placement="left"
          open={addRecordsDerawer}
          onClose={() => setAddRecordsDrawer(false)}
          size={windowSize >= 750 ? "large" : null}
          width={windowSize < 750 ? windowSize - 50 : null}
        >
          <AddNewCard onDataUpdate={dataUpdated}/>
        </Drawer>
        <Drawer
          placement="right"
          open={drawer}
          onClose={closeDrawer}
          size={windowSize >= 750 ? "large" : null}
          width={windowSize < 750 ? windowSize - 50 : null}
        >
          <CardControl
            id={drawer.id}
            onDataUpdate={dataUpdated}
            onCloseDrawer={closeDrawer}
          />
        </Drawer>

        <TableFilters onFilterDate={filterDate} onFilterSearch={filterSearch} />
        <Space></Space>
        <br />
        <AdminCardsListTable
          onDataUpdate={dataUpdated}
          data={tableData}
          onSort={sort}
          onPageChange={pageChange}
          currentPage={currentPage}
          loading={cards.loading}
          onOpenDrawer={openDrawer}
          cardStats={{
            totalIssued,
            activeIssued,
            inactiveIssued,
            loading: statsLoading,
          }}
        />
      </>
    </Badge.Ribbon>
  );
}

export default AdminAllUsersCards;
